import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { addDays, addMonths, endOfDay, format, isSameDay, startOfDay } from 'date-fns';
import { CalendarCheck, Clock, FileText, RefreshCw } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import apiClient from '../../services/api';
import { getPublishedRoster, getRosterPublicationStatus, RosterPublicationStatus, Shift } from '../../services/rosterService';
import RosterAvailabilityNotice from './RosterAvailabilityNotice';
import { getStaffShiftLabel } from '../../utils/rosterStyles';

const shiftColors: Record<string, { bar: string; bg: string; text: string; badge: string }> = {
    CLEARER: { bar: 'bg-blue-500', bg: 'bg-blue-50', text: 'text-blue-900', badge: 'bg-blue-100 text-blue-700' },
    LHR: { bar: 'bg-emerald-500', bg: 'bg-emerald-50', text: 'text-emerald-900', badge: 'bg-emerald-100 text-emerald-700' },
    LGW: { bar: 'bg-sky-500', bg: 'bg-sky-50', text: 'text-sky-900', badge: 'bg-sky-100 text-sky-700' },
    CONTROL: { bar: 'bg-purple-500', bg: 'bg-purple-50', text: 'text-purple-900', badge: 'bg-purple-100 text-purple-700' },
    OFFICE: { bar: 'bg-indigo-500', bg: 'bg-indigo-50', text: 'text-indigo-900', badge: 'bg-indigo-100 text-indigo-700' },
    WAREHOUSE: { bar: 'bg-amber-500', bg: 'bg-amber-50', text: 'text-amber-900', badge: 'bg-amber-100 text-amber-700' },
};

const defaultColor = { bar: 'bg-slate-400', bg: 'bg-slate-50', text: 'text-slate-800', badge: 'bg-slate-100 text-slate-600' };

const MySchedule = () => {
    const { user } = useAuth();
    const [shifts, setShifts] = useState<Shift[]>([]);
    const [todayTimesheet, setTodayTimesheet] = useState<any>(null);
    const [publicationStatus, setPublicationStatus] = useState<RosterPublicationStatus | null>(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState('');

    const loadMySchedule = useCallback(async (showRefresh = false) => {
        if (!user?.id) return;
        if (showRefresh) setRefreshing(true); else setLoading(true);
        setError('');

        try {
            const now = new Date();
            const nextMonth = addMonths(now, 1);
            const [currentStatus, ...rosterResponses] = await Promise.all([
                getRosterPublicationStatus(now.getFullYear(), now.getMonth() + 1),
                getPublishedRoster(now.getFullYear(), now.getMonth() + 1),
                getPublishedRoster(nextMonth.getFullYear(), nextMonth.getMonth() + 1).catch(() => null),
            ]);
            setPublicationStatus(currentStatus);

            const seen = new Set<string>();
            const upcoming = rosterResponses
                .flatMap(data => data?.staff?.find((member: any) => member.id === user.id)?.shifts || {})
                .flatMap((shiftMap: Record<string, Shift>) => Object.values(shiftMap || {}))
                .filter((shift: Shift) => {
                    if (!shift?.id || seen.has(shift.id)) return false;
                    seen.add(shift.id);
                    const start = new Date(shift.startTime);
                    return start >= startOfDay(now) && start <= endOfDay(addDays(now, 30));
                })
                .sort((a: Shift, b: Shift) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());

            const timesheets = await apiClient.get('/timesheets/my').catch(() => ({ data: { data: [] } }));
            const todayShift = upcoming.find((shift: Shift) => isSameDay(new Date(shift.startTime), now));
            const todayKey = format(now, 'yyyy-MM-dd');
            const submittedToday = (timesheets.data.data || []).find((ts: any) =>
                ts.shiftId === todayShift?.id || ts.shiftDate?.startsWith(todayKey)
            );

            setShifts(upcoming);
            setTodayTimesheet(submittedToday || null);
        } catch (err: any) {
            setError(err.response?.data?.error?.message || err.message || 'Failed to load your confirmed schedule');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [user?.id]);

    useEffect(() => { loadMySchedule(); }, [loadMySchedule]);

    const todayShift = useMemo(
        () => shifts.find(shift => isSameDay(new Date(shift.startTime), new Date())) || null,
        [shifts]
    );
    const upcomingShifts = useMemo(
        () => shifts.filter(shift => !isSameDay(new Date(shift.startTime), new Date())).slice(0, 12),
        [shifts]
    );
    const canSubmitToday = Boolean(todayShift && !todayTimesheet);

    if (loading) {
        return (
            <div className="max-w-3xl mx-auto space-y-3">
                <div className="h-8 bg-slate-200 rounded-lg w-1/2 animate-pulse" />
                {[1, 2, 3].map(i => (
                    <div key={i} className="bg-white rounded-lg border border-slate-200 p-4 animate-pulse">
                        <div className="h-4 bg-slate-200 rounded w-1/3 mb-2" />
                        <div className="h-3 bg-slate-100 rounded w-1/2" />
                    </div>
                ))}
            </div>
        );
    }

    const ShiftCard = ({ shift, today = false }: { shift: Shift; today?: boolean }) => {
        const label = getStaffShiftLabel(shift);
        const color = shiftColors[label] ?? defaultColor;
        return (
            <div className={`bg-white rounded-lg border border-slate-200 overflow-hidden ${today ? 'shadow-sm' : ''}`}>
                <div className={`flex items-stretch ${color.bg}`}>
                    <div className={`w-1.5 ${color.bar}`} />
                    <div className="flex-1 min-w-0 p-4">
                        <div className="flex flex-wrap items-center gap-2">
                            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${color.badge}`}>{label}</span>
                            {today && <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-blue-600 text-white">Today</span>}
                        </div>
                        <p className={`mt-2 text-lg font-bold ${color.text}`}>Rostered shift</p>
                        <p className="text-sm text-slate-500">{format(new Date(shift.startTime), 'EEEE, d MMM yyyy')}</p>
                        {shift.notes && <p className="text-xs text-slate-500 mt-1 italic">{shift.notes}</p>}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="max-w-3xl mx-auto space-y-5">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">Schedule</h1>
                    <p className="text-sm text-slate-500 mt-1">Today and upcoming confirmed shifts</p>
                </div>
                <button
                    onClick={() => loadMySchedule(true)}
                    disabled={refreshing}
                    className="p-2 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                    aria-label="Refresh"
                >
                    <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                </button>
            </div>

            {error && <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">{error}</div>}

            <section className="space-y-3">
                <div className="flex items-center gap-2 text-slate-700">
                    <CalendarCheck className="w-5 h-5" />
                    <h2 className="text-sm font-bold uppercase tracking-wide">Today</h2>
                </div>

                {publicationStatus && publicationStatus !== 'PUBLISHED' ? (
                    <RosterAvailabilityNotice status={publicationStatus} />
                ) : todayShift ? (
                    <div className="space-y-3">
                        <ShiftCard shift={todayShift} today />
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <Link
                                to={canSubmitToday ? `/timesheets/submit?shiftId=${todayShift.id}` : '/timesheets/my'}
                                className={`rounded-lg px-4 py-3 font-semibold text-center ${canSubmitToday ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-500'}`}
                            >
                                {canSubmitToday ? 'Submit hours' : `Timesheet ${todayTimesheet?.status || 'submitted'}`}
                            </Link>
                            <Link to="/my-roster" className="rounded-lg px-4 py-3 font-semibold text-center bg-white border border-slate-200 text-slate-700">
                                View my roster
                            </Link>
                        </div>
                    </div>
                ) : (
                    <div className="bg-white border border-slate-200 rounded-lg p-6 text-center">
                        <Clock className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                        <p className="font-semibold text-slate-700">No shift today</p>
                        <p className="text-sm text-slate-400 mt-1">Your next confirmed shifts are shown below.</p>
                    </div>
                )}
            </section>

            <section className="space-y-3">
                <div className="flex items-center gap-2 text-slate-700">
                    <FileText className="w-5 h-5" />
                    <h2 className="text-sm font-bold uppercase tracking-wide">Upcoming</h2>
                </div>

                {upcomingShifts.length === 0 ? (
                    <div className="bg-white border border-slate-200 rounded-lg p-6 text-center text-slate-500">
                        No confirmed upcoming shifts in the next 30 days.
                    </div>
                ) : (
                    <div className="space-y-3">
                        {upcomingShifts.map(shift => <ShiftCard key={shift.id} shift={shift} />)}
                    </div>
                )}
            </section>
        </div>
    );
};

export default MySchedule;
